import React, { useCallback } from 'react';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RecordCardList from '../RecordCards/RecordCardList';
import { RECORD_KIND } from '../../utils/recordCards';
import { getDisplayUnits } from '../../utils/userLotUnits';

const iconSx = { fontSize: 20 };

/**
 * Vista «por registro» del detalle de lista: una card por lote (contenido o
 * ítem directo) y una por UF. Mismas acciones que `ListDetailLotCard`:
 * - Lista propia: Ubicar, Editar (UF), Ver lote, Eliminar de la lista (si no
 *   es espacio ajeno; la card de contenido de un lote con UF no se quita: no es
 *   un ítem de la lista).
 * - Compartida conmigo (`readOnly`): Ubicar, Ver detalle (UF), Ver lote.
 */
export default function ListDetailRecordCards({
  items,
  layout,
  readOnly = false,
  isRecordLocated,
  onLocateLot,
  onOpenUnit,
  onOpenLot,
  onRemoveUnit,
  onRemoveLot,
  updatingUnitId = null,
  onHoverLot,
  highlightedLotId = null,
}) {
  const handleOpen = useCallback((item) => {
    if (item.record?.record_kind === RECORD_KIND.UNIT) {
      onOpenUnit?.(item.lot, item.unit);
      return;
    }
    onOpenLot?.(item.lot);
  }, [onOpenUnit, onOpenLot]);

  const getActions = (item) => {
    const isUnit = item.record?.record_kind === RECORD_KIND.UNIT;
    const located = isRecordLocated?.(item);
    const actions = [{
      key: 'locate',
      label: located ? 'Quitar marca del mapa' : 'Ubicar en el mapa',
      icon: <MyLocationOutlinedIcon sx={iconSx} />,
      onClick: () => onLocateLot?.(item.lotId, item.lot, item.key),
    }];
    if (isUnit) {
      actions.push({
        key: 'open',
        label: readOnly ? 'Ver detalle' : 'Editar',
        icon: readOnly ? <VisibilityOutlinedIcon sx={iconSx} /> : <EditIcon sx={iconSx} />,
        onClick: handleOpen,
      });
    }
    if (onOpenLot) {
      actions.push({
        key: 'lot',
        label: !isUnit && item.hasLotContent ? 'Ver contenido del lote' : 'Ver lote',
        icon: <VisibilityOutlinedIcon sx={iconSx} />,
        onClick: () => onOpenLot(item.lot),
      });
    }
    if (!readOnly) {
      const unitId = item.unit?.id;
      if (isUnit && onRemoveUnit && unitId) {
        const removing = updatingUnitId === unitId;
        actions.push({
          key: 'remove',
          label: 'Eliminar de la lista',
          icon: <DeleteOutlineIcon sx={iconSx} />,
          disabled: removing,
          loading: removing,
          onClick: () => onRemoveUnit(item.unit),
        });
      } else if (!isUnit && onRemoveLot && getDisplayUnits(item.lot).length === 0) {
        const removing = updatingUnitId === `lot:${item.lotId}`;
        actions.push({
          key: 'remove',
          label: 'Eliminar de la lista',
          icon: <DeleteOutlineIcon sx={iconSx} />,
          disabled: removing,
          loading: removing,
          onClick: () => onRemoveLot(item.lot),
        });
      }
    }
    return actions;
  };

  return (
    <RecordCardList
      items={items}
      layout={layout}
      isItemLocated={isRecordLocated}
      getActions={getActions}
      onOpen={handleOpen}
      onHoverLot={onHoverLot}
      highlightedLotId={highlightedLotId}
    />
  );
}
